/**
 * Messages between the Hub and the merchant app running inside the Hub's merchant dashboard preview iframe.
 * Keep in sync with `mezza-hub/app/utils/merchantDashboardPreviewBridge.ts`.
 */

/**
 * Request header carrying the short-lived preview token the Hub minted, sent instead of `Authorization`. The token
 * names the team member and the previewed merchant; the iframe never sees the team member's Firebase token.
 */
export const HUB_PREVIEW_TOKEN_HEADER = "X-Hub-Preview-Token";

/** Request header with the location ids picked in the preview's location picker (comma-separated; absent = all). */
export const HUB_PREVIEW_LOCATIONS_HEADER = "X-Hub-Preview-Locations";

/** Path the Hub loads in the iframe. */
export const HUB_PREVIEW_PATH = "/hub-preview";

export interface HubPreviewVisibility {
  readonly hiddenPages: readonly string[];
  readonly hiddenMetrics: readonly string[];
  readonly hiddenReports: readonly string[];
}

/** Hub → merchant app. */
export type HubToPreviewMessage =
  | ({
      readonly type: "mezza-preview:init";
      readonly merchantId: string;
      readonly previewToken: string;
      /** ISO UTC. */
      readonly expiresAt: string;
    } & HubPreviewVisibility)
  | ({ readonly type: "mezza-preview:visibility" } & HubPreviewVisibility)
  | { readonly type: "mezza-preview:token"; readonly previewToken: string; readonly expiresAt: string }
  | { readonly type: "mezza-preview:navigate"; readonly path: string };

/** Merchant app → Hub. */
export type PreviewToHubMessage =
  | { readonly type: "mezza-preview:ready" }
  | { readonly type: "mezza-preview:painted" }
  | { readonly type: "mezza-preview:token-request" }
  | { readonly type: "mezza-preview:navigated"; readonly path: string };

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const hasVisibility = (data: Record<string, unknown>): boolean =>
  isStringArray(data.hiddenPages) && isStringArray(data.hiddenMetrics) && isStringArray(data.hiddenReports);

const hasPreviewToken = (data: Record<string, unknown>): boolean =>
  typeof data.previewToken === "string" &&
  typeof data.expiresAt === "string" &&
  !Number.isNaN(Date.parse(data.expiresAt));

export function isHubToPreviewMessage(data: unknown): data is HubToPreviewMessage {
  if (typeof data !== "object" || data === null) return false;
  const message = data as Record<string, unknown>;

  switch (message.type) {
    case "mezza-preview:init":
      return typeof message.merchantId === "string" && hasPreviewToken(message) && hasVisibility(message);
    case "mezza-preview:visibility":
      return hasVisibility(message);
    case "mezza-preview:token":
      return hasPreviewToken(message);
    case "mezza-preview:navigate":
      return typeof message.path === "string" && message.path.startsWith("/dashboard");
    default:
      return false;
  }
}

/** The Hub allowed to frame this merchant app, by the host it is served from; null where previews are off. */
export function getHubOrigin(hostname: string): string | null {
  switch (hostname) {
    case "localhost":
    case "127.0.0.1":
      return "http://localhost:3001";
    case "merchant.qa.mezza.cloud":
      return "https://hub.qa.mezza.cloud";
    case "merchant.staging.mezzapay.com":
      return "https://hub.staging.mezzapay.com";
    case "merchant.mezzapay.com":
      return "https://hub.mezzapay.com";
    default:
      return null;
  }
}
